/**
 * Fixed KPI Period Dropdown Selector Component
 * Ensures correct date ranges for month/quarter/year selection
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface KpiPeriodDropdownsProps {
  onPeriodSelect: (start: string, end: string, label: string) => void;
  className?: string;
}

const isLeapYear = (year: number) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const getLastDayOfMonth = (year: number, month: number) => {
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28;
  }
  if ([4, 6, 9, 11].includes(month)) {
    return 30;
  }
  return 31;
};

export const KpiPeriodDropdownsFixed: React.FC<KpiPeriodDropdownsProps> = ({
  onPeriodSelect,
  className
}) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-12
  const currentQuarter = Math.ceil(currentMonth / 3);

  // Initialize with last month as default
  const getDefaultMonth = () => {
    const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const year = currentMonth === 1 ? currentYear - 1 : currentYear;
    return { month: lastMonth, year };
  };

  const defaultValues = getDefaultMonth();
  
  // State for each dropdown
  const [selectedType, setSelectedType] = useState<'month' | 'quarter' | 'year'>('month');
  const [selectedYear, setSelectedYear] = useState(defaultValues.year);
  const [selectedMonth, setSelectedMonth] = useState(defaultValues.month);
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter);

  // Generate available years (current year back to 2024)
  const availableYears = Array.from(
    { length: currentYear - 2023 }, 
    (_, i) => currentYear - i
  );

  // Generate months
  const months = [
    { value: 1, label: 'Januar' },
    { value: 2, label: 'Februar' },
    { value: 3, label: 'März' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Dezember' }
  ];

  // Generate quarters
  const quarters = [
    { value: 1, label: 'Q1 (Jan-Mär)' },
    { value: 2, label: 'Q2 (Apr-Jun)' },
    { value: 3, label: 'Q3 (Jul-Sep)' },
    { value: 4, label: 'Q4 (Okt-Dez)' }
  ];

  // Calculate period dates based on selection
  const calculatePeriod = useCallback(() => {
    let start: string;
    let end: string;
    let label: string;

    const year = selectedYear;

    switch (selectedType) {
      case 'month': {
        const month = selectedMonth;
        const lastDay = getLastDayOfMonth(year, month);
        start = `${year}-${String(month).padStart(2, '0')}-01`;
        end = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        const monthName = months.find(m => m.value === month)?.label || '';
        label = `${monthName} ${year}`;
        break;
      }

      case 'quarter': {
        const quarter = selectedQuarter;
        const startMonth = (quarter - 1) * 3 + 1;
        const endMonth = startMonth + 2;
        const lastDayOfQuarter = getLastDayOfMonth(year, endMonth);

        start = `${year}-${String(startMonth).padStart(2, '0')}-01`;
        end = `${year}-${String(endMonth).padStart(2, '0')}-${String(lastDayOfQuarter).padStart(2, '0')}`;
        label = `Q${quarter} ${year}`;
        break;
      }

      case 'year': {
        start = `${year}-01-01`;
        end = `${year}-12-31`;
        label = `${year}`;
        break;
      }
    }

    return { start, end, label };
  }, [selectedType, selectedYear, selectedMonth, selectedQuarter, months]);

  // Update parent when selection changes
  useEffect(() => {
    const { start, end, label } = calculatePeriod();
    onPeriodSelect(start, end, label);
  }, [calculatePeriod, onPeriodSelect]);

  // Filter available months/quarters based on selected year
  const getAvailableMonths = () => {
    if (selectedYear === currentYear) {
      // Only show months up to current month for current year
      return months.filter(m => m.value <= currentMonth);
    }
    return months;
  };

  const getAvailableQuarters = () => {
    if (selectedYear === currentYear) {
      // Only show quarters up to current quarter for current year
      return quarters.filter(q => q.value <= currentQuarter);
    }
    return quarters;
  };

  // Adjust selected month/quarter when year changes
  useEffect(() => {
    if (selectedYear === currentYear) {
      if (selectedMonth > currentMonth) {
        setSelectedMonth(currentMonth);
      }
      if (selectedQuarter > currentQuarter) {
        setSelectedQuarter(currentQuarter);
      }
    }
  }, [selectedYear, currentYear, currentMonth, currentQuarter, selectedMonth, selectedQuarter]);

  return (
    <div className={cn("bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg", className)}>
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        <h3 className="text-lg font-semibold">Zeitraum auswählen</h3>
      </div>

      <div className="space-y-4">
        {/* Period Type Selector */}
        <div className="flex gap-2">
          {(['month', 'quarter', 'year'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                selectedType === type
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              )}
            >
              {type === 'month' ? 'Monat' : type === 'quarter' ? 'Quartal' : 'Jahr'}
            </button>
          ))}
        </div>

        {/* Dropdowns */}
        <div className="grid grid-cols-2 gap-4">
          {/* Year Dropdown (always visible) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Jahr
            </label>
            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="block w-full px-3 py-2 pr-8 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none cursor-pointer"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Month/Quarter Dropdown (conditional) */}
          {selectedType !== 'year' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {selectedType === 'month' ? 'Monat' : 'Quartal'}
              </label>
              <div className="relative">
                {selectedType === 'month' ? (
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="block w-full px-3 py-2 pr-8 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none cursor-pointer"
                  >
                    {getAvailableMonths().map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
                    className="block w-full px-3 py-2 pr-8 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none cursor-pointer"
                  >
                    {getAvailableQuarters().map(quarter => (
                      <option key={quarter.value} value={quarter.value}>{quarter.label}</option>
                    ))}
                  </select>
                )}
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};